/**
 * Link BIM Tool
 *
 * Links another Yap project document (.yap/.json) into the current project
 * as a live read-only reference — the native counterpart of Revit's
 * "Link Revit". The foreign document's elements are instantiated into the
 * host model with collision-free ids, tagged with the LinkInstance id, and
 * the wrapper footprint carries the measured real extents so Manage Links
 * drives the whole instance (hide / unload / remove).
 *
 * Revit binaries (.rvt/.rfa) cannot be decoded in this build: they are
 * refused with an explicit "export to IFC" message — no schematic box is
 * ever drawn for an unreadable file.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager, LinkInstance } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import {
    parseLinkedProjectText,
    instantiateLinkedElements,
    bboxSize,
    LinkedProjectPayload,
} from '../services/linked/LinkedProjectService';
import { CadLinkStore } from '../services/cad/CadLinkStore';
import i18n from '../i18n';

export interface LinkedProjectGeometryData {
    linkId: string;
    origin: [number, number, number];
    scale: [number, number, number];
    rotation: [number, number, number];
    /** Real measured extents of the linked model. */
    boxSize: [number, number, number];
    /** Marks the wrapper as an outline over real linked member geometry. */
    linkedProject: {
        memberCount: number;
        source: string;
    };
}

interface LinkedBimElement extends Element {
    geometryData: LinkedProjectGeometryData;
}

export class LinkBimTool extends BaseTool {
    id = 'link_bim';
    name = 'Link BIM';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for Link BIM.
        // Only native documents are offered — binary Revit files cannot be
        // decoded in this build.
        void this.pickFile('.yap,.json').then(async (file) => {
            if (file) {
                await this.linkProjectFile(file);
            }
            this.exitToSelect();
        });
    }

    /**
     * Full link flow with a real File: parse the document, instantiate its
     * elements, bind the wrapper footprint for Manage Links.
     */
    public async linkProjectFile(file: File): Promise<LinkInstance | null> {
        if (/\.rvt$|\.rfa$/i.test(file.name)) {
            this.notify(i18n.t('tools.notify.revitBinaryUnsupported', { fileName: file.name }), 'error');
            return null;
        }
        const text = await file.text();
        const payload = parseLinkedProjectText(text);
        if (!payload) {
            this.notify(i18n.t('tools.notify.bimLinkParseFailed', { fileName: file.name }), 'error');
            return null;
        }
        const link = this.executeLinkFromPayload(file.name, payload, file.size);
        if (link) {
            const docId = link.documentId;
            if (docId) CadLinkStore.setSource(docId, file, file.name);
        }
        return link;
    }

    /**
     * Programmatic entry with an already-parsed payload (macros/tests).
     * Registers the link, instantiates every foreign element into the host
     * model and binds the wrapper footprint.
     */
    public executeLinkFromPayload(filePath: string, payload: LinkedProjectPayload, size = 0): LinkInstance | null {
        const linkInstance = this.linkManager.linkFile('BIM', filePath, {
            positioning: 'OriginToOrigin',
            size
        });

        const { elements, bbox } = instantiateLinkedElements(payload.elementEntries, linkInstance.id);
        for (const el of elements) {
            this.context?.bimStore.addElement(el as any);
        }

        const wrapper = this.createLinkElement(linkInstance, elements.length, bboxSize(bbox));
        this.linkManager.setDocumentId(linkInstance.id, wrapper.id);

        this.notify(
            i18n.t('tools.notify.bimLinkLoaded', { fileName: filePath, count: elements.length }),
            'success'
        );
        return linkInstance;
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: nothing to place interactively.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createLinkElement(link: LinkInstance, memberCount: number, boxSize: [number, number, number]): LinkedBimElement {
        const linkElement: LinkedBimElement = {
            id: uuidv4(),
            category: 'BIM Links',
            type: 'Linked BIM Model: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Status': link.status,
                'Members': String(memberCount),
                'Shared Site': 'Internal'
            },
            constraints: [],
            geometryData: {
                linkId: link.id,
                origin: [0, 0, 0],
                scale: [1, 1, 1],
                rotation: [0, 0, 0],
                boxSize,
                linkedProject: {
                    memberCount,
                    source: link.path,
                },
            }
        };

        this.context?.bimStore.addElement(linkElement as any);
        // Bind the wrapper element to the registry entry so Manage Links can
        // hide/unhide/reload/remove the exact scene object.
        this.linkManager.setDocumentId(link.id, linkElement.id);
        return linkElement;
    }
}
